import { LivePinVehicleData, ParsedVehicleData } from './interfaces/LivePin.interface';

export class LivePinService {
    /**
     * Fetch vehicle data from LivePin API
     * @param url - LivePin share link URL
     * @returns Promise<LivePinVehicleData | null>
     */
    async fetchVehicleData(url: string): Promise<LivePinVehicleData | null> {
        try {
            console.log(`🔗 Fetching LivePin data from: ${url}`);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                mode: 'cors', // Handle CORS if needed
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: LivePinVehicleData = await response.json();
            console.log('✅ LivePin data fetched successfully:', data);
            console.log('🔍 LivePin data structure:', {
                hasLatitude: 'latitude' in data,
                hasLongitude: 'longitude' in data,
                hasLocationRecords: 'location_records' in data,
                locationRecordsLength: data.location_records?.length || 0,
                keys: Object.keys(data)
            });

            // Log the actual latitude and longitude values
            console.log('🔍 LivePin coordinates:', {
                latitude: data.latitude,
                longitude: data.longitude,
                firstLocationRecord: data.location_records?.[0] ? {
                    latitude: data.location_records[0].latitude,
                    longitude: data.location_records[0].longitude
                } : null
            });

            return data;
        } catch (error) {
            console.error('❌ Error fetching LivePin data:', error);
            return null;
        }
    }

    /**
     * Parse LivePin data into standardized format
     * @param data - Raw LivePin API response
     * @returns ParsedVehicleData
     */
    parseVehicleData(data: LivePinVehicleData): ParsedVehicleData {
        // Get the latest location from location_records array
        const latestLocation = data.location_records && data.location_records.length > 0
            ? data.location_records[data.location_records.length - 1]
            : null;

        const parsedData = {
            vehicleNumber: data.vehicle_data?.vehicle_no || 'Unknown Vehicle',
            latitude: latestLocation?.latitude || data.latitude || 0,
            longitude: latestLocation?.longitude || data.longitude || 0,
            speed: latestLocation?.speed || data.speed || 0,
            status: data.vehicle_status || 'UNKNOWN',
            battery: data.battery_percent || 0,
            timestamp: latestLocation?.timestamp || data.timestamp || Date.now(),
            address: data.address || 'Unknown Location',
            angle: latestLocation?.angle || data.angle || 0,
            acc: latestLocation?.acc || data.acc || false,
            locationHistory: data.location_records?.map(record => ({
                latitude: record.latitude,
                longitude: record.longitude,
                timestamp: record.timestamp,
                speed: record.speed,
                angle: record.angle,
                acc: record.acc
            })) || []
        };

        console.log('🔍 Parsed LivePin data:', {
            latitude: parsedData.latitude,
            longitude: parsedData.longitude,
            speed: parsedData.speed,
            status: parsedData.status,
            battery: parsedData.battery,
            hasValidCoordinates: parsedData.latitude !== 0 && parsedData.longitude !== 0
        });

        return parsedData;
    }

    /**
     * Validate LivePin URL format
     * @param url - URL to validate
     * @returns boolean
     */
    isValidLivePinUrl(url: string): boolean {
        const livePinPattern = /^https:\/\/api\.livepin\.in\/api\/share-links\/[a-zA-Z0-9\-]+$/;
        return livePinPattern.test(url);
    }

    /**
     * Extract vehicle identifier from LivePin URL
     * @param url - LivePin URL
     * @returns string | null
     */
    extractVehicleId(url: string): string | null {
        const match = url.match(/\/share-links\/([a-zA-Z0-9\-]+)$/);
        return match ? match[1] : null;
    }
}
